import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { AdminLayout } from '../components/AdminLayout';
import { toast } from 'sonner';
import { 
  ArrowLeft, Save, Mail, User, MapPin, Phone, Globe, 
  Instagram, Shield, Calendar, Loader2, Send, AlertCircle,
  CheckCircle, XCircle
} from 'lucide-react';

const API_URL = "";

// Status and type options
const MEMBERSHIP_STATUSES = ['active', 'lifetime', 'expired', 'inactive'];
const MEMBERSHIP_TYPES = ['artist', 'patron', 'instructor', 'board', 'general'];
const ACCESS_LEVELS = ['Member', 'Administrator'];

// Status badge colors
const statusColors = {
  active: 'bg-green-100 text-green-800 border-green-200',
  lifetime: 'bg-amber-100 text-amber-800 border-amber-200',
  expired: 'bg-red-100 text-red-800 border-red-200',
  inactive: 'bg-stone-100 text-stone-600 border-stone-200'
};

export const AdminMemberEdit = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [member, setMember] = useState(null);
  const [error, setError] = useState('');
  const [newNote, setNewNote] = useState('');
  
  const isAdmin = user?.access && ['Administrator', 'Webmaster', 'Admin'].includes(user.access);

  // Fetch member data
  useEffect(() => {
    const fetchMember = async () => {
      if (!token || !memberId) return;
      
      try {
        const response = await fetch(`${API_URL}/api/admin/members/${memberId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setMember(data);
        } else {
          setError('Member not found');
        }
      } catch (err) {
        setError('Failed to load member data');
      } finally {
        setLoading(false);
      }
    };
    
    if (isAdmin) {
      fetchMember();
    } else {
      navigate('/members/directory');
    }
  }, [token, memberId, isAdmin, navigate]);

  // Handle field change
  const handleChange = (field, value) => {
    setMember(prev => ({ ...prev, [field]: value }));
  };

  // Save member
  const handleSave = async () => {
    setSaving(true);
    
    try {
      const updateData = {
        first_name: member.first_name,
        last_name: member.last_name,
        email: member.email,
        address: member.address,
        city: member.city,
        state: member.state,
        zip: member.zip,
        phone_home: member.phone_home,
        phone_mobile: member.phone_mobile,
        phone_work: member.phone_work,
        website: member.website,
        instagram: member.instagram,
        membership_status: member.membership_status,
        membership_type: member.membership_type,
        is_active_member: member.membership_status === 'active' || member.membership_status === 'lifetime',
        is_lifetime_member: member.membership_status === 'lifetime',
        newsletter_opt_in: member.newsletter_opt_in,
        access: member.access,
        expiration_date: member.expiration_date,
        position: member.position
      };
      
      const response = await fetch(`${API_URL}/api/admin/members/${memberId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        toast.success('Member updated successfully');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update member');
      }
    } catch (err) {
      toast.error('An error occurred while saving');
    } finally {
      setSaving(false);
    }
  };

  // Append note
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/members/${memberId}/append-note?note=${encodeURIComponent(newNote)}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMember(prev => ({ ...prev, legacy_comments: data.legacy_comments }));
        setNewNote('');
        toast.success('Note added');
      } else {
        toast.error('Failed to add note');
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  // Send password setup email
  const handleSendPasswordSetup = async () => {
    setSendingEmail(true);
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/members/${memberId}/send-password-setup`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Password setup email sent to ${data.email}`);
      } else {
        toast.error(data.detail || 'Failed to send email');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return null; // AdminLayout handles loading
  }

  if (error || !member) {
    return (
      <AdminLayout title="Member Not Found">
        <div className="min-h-[50vh] flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">{error || 'Member not found'}</h2>
              <Link to="/admin/members">
                <Button className="mt-4">Back to Members</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={`Edit: ${member.first_name} ${member.last_name}`} subtitle={member.email}>
      <div data-testid="admin-member-edit-page">
      {/* Actions Bar */}
      <div className="bg-white border-b border-stone-200 py-3">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <Link to="/admin/members">
              <Button variant="ghost" size="sm">
                <ArrowLeft size={18} className="mr-1" /> Back to Members
              </Button>
            </Link>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-cafa-red hover:bg-cafa-red-900"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save size={18} className="mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User size={20} /> Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name</Label>
                    <Input
                      value={member.first_name || ''}
                      onChange={(e) => handleChange('first_name', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <Input
                      value={member.last_name || ''}
                      onChange={(e) => handleChange('last_name', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={member.email || ''}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Position/Title</Label>
                  <Input
                    value={member.position || ''}
                    onChange={(e) => handleChange('position', e.target.value)}
                    placeholder="e.g., Artist, Instructor, Board Member"
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin size={20} /> Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Address</Label>
                  <Input
                    value={member.address || ''}
                    onChange={(e) => handleChange('address', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input
                      value={member.city || ''}
                      onChange={(e) => handleChange('city', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input
                      value={member.state || ''}
                      onChange={(e) => handleChange('state', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>ZIP</Label>
                    <Input
                      value={member.zip || ''}
                      onChange={(e) => handleChange('zip', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="flex items-center gap-1"><Phone size={14} /> Home</Label>
                    <Input
                      value={member.phone_home || ''}
                      onChange={(e) => handleChange('phone_home', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1"><Phone size={14} /> Mobile</Label>
                    <Input
                      value={member.phone_mobile || ''}
                      onChange={(e) => handleChange('phone_mobile', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1"><Phone size={14} /> Work</Label>
                    <Input
                      value={member.phone_work || ''}
                      onChange={(e) => handleChange('phone_work', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-1"><Globe size={14} /> Website</Label>
                    <Input
                      value={member.website || ''}
                      onChange={(e) => handleChange('website', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1"><Instagram size={14} /> Instagram</Label>
                    <Input
                      value={member.instagram || ''}
                      onChange={(e) => handleChange('instagram', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle>Notes & History</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Add New Note</Label>
                  <div className="flex gap-2 mt-1">
                    <Textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add a note about this member..."
                      rows={2}
                      className="flex-1"
                    />
                    <Button onClick={handleAddNote} disabled={!newNote.trim()}>
                      Add
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Legacy Comments & Notes</Label>
                  <div className="mt-1 p-3 bg-stone-50 rounded-lg border border-stone-200 max-h-64 overflow-y-auto">
                    <pre className="text-sm text-stone-700 whitespace-pre-wrap font-sans">
                      {member.legacy_comments || 'No notes yet'}
                    </pre>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Status & Actions */}
          <div className="space-y-6">
            {/* Membership Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield size={20} /> Membership
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Status</Label>
                  <select
                    value={member.membership_status || 'inactive'}
                    onChange={(e) => handleChange('membership_status', e.target.value)}
                    className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 text-sm"
                  >
                    {MEMBERSHIP_STATUSES.map(status => (
                      <option key={status} value={status} className="capitalize">{status}</option>
                    ))}
                  </select>
                  <div className="mt-2">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border capitalize ${statusColors[member.membership_status] || statusColors.inactive}`}>
                      {member.membership_status || 'inactive'}
                    </span>
                  </div>
                </div>
                
                <div>
                  <Label>Type</Label>
                  <select
                    value={member.membership_type || 'general'}
                    onChange={(e) => handleChange('membership_type', e.target.value)}
                    className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 text-sm"
                  >
                    {MEMBERSHIP_TYPES.map(type => (
                      <option key={type} value={type} className="capitalize">{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Access Level</Label>
                  <select
                    value={member.access || 'Member'}
                    onChange={(e) => handleChange('access', e.target.value)}
                    className="mt-1 w-full rounded-md border border-stone-300 px-3 py-2 text-sm"
                  >
                    {ACCESS_LEVELS.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label className="flex items-center gap-1"><Calendar size={14} /> Expiration Date</Label>
                  <Input
                    type="date"
                    value={member.expiration_date || ''}
                    onChange={(e) => handleChange('expiration_date', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Communications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail size={20} /> Communications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Newsletter Opt-in</Label>
                  <button
                    onClick={() => handleChange('newsletter_opt_in', !member.newsletter_opt_in)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${member.newsletter_opt_in ? 'bg-green-500' : 'bg-stone-300'}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${member.newsletter_opt_in ? 'translate-x-7' : 'translate-x-1'}`} />
                  </button>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  {member.newsletter_opt_in ? (
                    <><CheckCircle size={16} className="text-green-500" /> Subscribed</>
                  ) : (
                    <><XCircle size={16} className="text-stone-400" /> Not subscribed</>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Account Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Account Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  onClick={handleSendPasswordSetup}
                  disabled={sendingEmail}
                  variant="outline"
                  className="w-full"
                >
                  {sendingEmail ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Send size={16} className="mr-2" />
                  )}
                  Send Password Setup Email
                </Button>
                
                <p className="text-xs text-stone-500">
                  {member.password_setup_sent_at 
                    ? `Last sent: ${new Date(member.password_setup_sent_at).toLocaleDateString()}`
                    : 'Never sent'}
                </p>
                
                <p className="text-xs text-stone-500">
                  {member.password_set_at 
                    ? `Password set: ${new Date(member.password_set_at).toLocaleDateString()}`
                    : member.password 
                      ? 'Has password (legacy)'
                      : 'No password set'}
                </p>
              </CardContent>
            </Card>

            {/* Meta Info */}
            <Card>
              <CardContent className="pt-4 text-xs text-stone-500 space-y-1">
                <p><strong>Member ID:</strong> {member.id}</p>
                {member.legacy_member_id && <p><strong>Legacy ID:</strong> {member.legacy_member_id}</p>}
                {member.source && <p><strong>Source:</strong> {member.source}</p>}
                {member.joined_date && <p><strong>Joined:</strong> {member.joined_date}</p>}
                {member.created_at && <p><strong>Created:</strong> {new Date(member.created_at).toLocaleDateString()}</p>}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      </div>
    </AdminLayout>
  );
};
